import { useTone } from '@/Components/Marketing/Tone';
import { cn } from '@/lib/utils';

// Headings take the ink colour, running text the softer body colour.
const HEADING = { light: 'text-mk-ink', dark: 'text-mk-night-text' };
const TEXT = { light: 'text-mk-body', dark: 'text-mk-night-body' };

// Tailwind's text-6xl / text-5xl / text-xl carry their own line-height, which
// would override the base leading from sm up; the sm: leading repeats it so the
// specified leading holds at every breakpoint.

/** The page headline. One per page, so an <h1> unless `as` says otherwise. */
export function Display({ as: Tag = 'h1', tone, className, ...props }) {
    const resolved = useTone(tone);

    return (
        <Tag
            className={cn(
                'text-[2.6rem] leading-[1.02] font-semibold tracking-[-0.035em] text-balance sm:text-6xl sm:leading-[1.02] lg:text-[4.5rem]',
                HEADING[resolved],
                className,
            )}
            {...props}
        />
    );
}

/** A section headline; an <h2> by default. */
export function Heading({ as: Tag = 'h2', tone, className, ...props }) {
    const resolved = useTone(tone);

    return (
        <Tag
            className={cn(
                'text-4xl leading-[1.05] font-semibold tracking-[-0.03em] text-balance sm:text-5xl sm:leading-[1.05]',
                HEADING[resolved],
                className,
            )}
            {...props}
        />
    );
}

/** The larger paragraph that follows a headline. */
export function Lead({ as: Tag = 'p', tone, className, ...props }) {
    const resolved = useTone(tone);

    return (
        <Tag
            className={cn(
                'max-w-[60ch] text-lg leading-relaxed text-pretty sm:text-xl sm:leading-relaxed',
                TEXT[resolved],
                className,
            )}
            {...props}
        />
    );
}

/** Running text. */
export function Body({ as: Tag = 'p', tone, className, ...props }) {
    const resolved = useTone(tone);

    return (
        <Tag
            className={cn('max-w-[65ch] text-base leading-relaxed text-pretty', TEXT[resolved], className)}
            {...props}
        />
    );
}
